import { Plus, Eye, Trash2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../../services/api';

const STATUS_STYLE = {
  valid:           { dot: 'bg-emerald-400', text: 'text-emerald-300', label: 'Valid' },
  expiring_soon:   { dot: 'bg-amber-400',   text: 'text-amber-300',   label: 'Expiring soon' },
  expired:         { dot: 'bg-rose-400',    text: 'text-rose-300',    label: 'Expired' },
};

function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatExpiry(s, status) {
  if (!s) return null;
  return `${status === 'expired' ? 'expired' : 'expires'} ${formatDate(s)}`;
}

async function viewDocument(docId) {
  const res = await authFetch(`/api/documents/${docId}/url`);
  if (!res.ok) {
    toast.error('Could not open document');
    return;
  }
  const body = await res.json();
  window.open(body.url, '_blank', 'noopener,noreferrer');
}

async function deleteDocument(docId, onChanged) {
  if (!window.confirm('Delete this document? You can re-upload it later.')) return;
  const res = await authFetch(`/api/documents/${docId}`, { method: 'DELETE' });
  if (res.ok) {
    toast.success('Document deleted');
    onChanged();
  } else {
    toast.error('Could not delete document');
  }
}

export default function CategoryPanel({ group, onUploadClick, onChanged }) {
  const expired = group.documents.some((d) => d.status === 'expired');
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-white font-medium">{group.label}</h3>
          {expired && <AlertTriangle size={14} className="text-rose-400" />}
        </div>
        <button
          onClick={onUploadClick}
          className="flex items-center gap-1 text-sm px-3 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
        >
          <Plus size={14} /> Upload
        </button>
      </div>
      {group.documents.length === 0 ? (
        <p className="text-sm text-slate-500">{group.description}</p>
      ) : (
        <ul className="space-y-2">
          {group.documents.map((d) => {
            const s = STATUS_STYLE[d.status] || STATUS_STYLE.valid;
            return (
              <li
                key={d.id}
                className="flex items-center justify-between py-2 border-b border-slate-800/60 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{d.filename}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />
                    <span className={s.text}>{s.label}</span>
                    {d.expires_at && <span>· {formatExpiry(d.expires_at, d.status)}</span>}
                    <span>· uploaded {formatDate(d.uploaded_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => viewDocument(d.id)}
                    className="text-slate-400 hover:text-emerald-400 p-1"
                    title="View"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => deleteDocument(d.id, onChanged)}
                    className="text-slate-400 hover:text-rose-400 p-1"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
